import { z } from 'zod';

// Login validation schema
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

// Size validation schema
export const sizeSchema = z.object({
  name: z
    .string()
    .min(1, 'Size name is required')
    .max(50, 'Size name must be less than 50 characters'),
  is_active: z.boolean().default(true),
});

// Catalogue validation schema
export const catalogueSchema = z.object({
  name: z
    .string()
    .min(1, 'Catalogue name is required')
    .max(100, 'Catalogue name must be less than 100 characters'),
  thumbnail_url: z
    .string()
    .min(1, 'Thumbnail is required'),
  pdf_url: z
    .string()
    .min(1, 'PDF file is required'),
  size_id: z
    .union([z.string(), z.number()])
    .refine((val) => val !== '' && val !== null && val !== undefined, {
      message: 'Please select a size',
    }),
  is_active: z.boolean().default(true),
});

// Contact update validation schema
export const contactUpdateSchema = z.object({
  status: z.enum(['new', 'viewed', 'responded', 'closed'], {
    errorMap: () => ({ message: 'Please select a valid status' }),
  }),
  admin_notes: z.string().max(1000, 'Notes must be less than 1000 characters').optional(),
});

// Enquiry update validation schema
export const enquiryUpdateSchema = z.object({
  status: z.enum(['new', 'contacted', 'converted', 'closed'], {
    errorMap: () => ({ message: 'Please select a valid status' }),
  }),
  admin_notes: z.string().max(1000, 'Notes must be less than 1000 characters').optional(),
});

/**
 * Validate form data against a schema
 * @param {z.ZodSchema} schema - Zod schema to validate against
 * @param {Object} data - Form data to validate
 * @returns {{ success: boolean, data?: Object, errors?: Object }}
 */
export const validateForm = (schema, data) => {
  try {
    const validated = schema.parse(data);
    return { success: true, data: validated };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = {};
      error.errors.forEach((err) => {
        const path = err.path.join('.');
        if (!errors[path]) {
          errors[path] = err.message;
        }
      });
      return { success: false, errors };
    }
    return { success: false, errors: { _form: 'Validation failed' } };
  }
};

/**
 * Get field error from validation errors object
 * @param {Object} errors - Validation errors object
 * @param {string} field - Field name
 * @returns {string|null}
 */
export const getFieldError = (errors, field) => {
  if (!errors) return null;
  return errors[field] || null;
};

export default {
  loginSchema,
  sizeSchema,
  catalogueSchema,
  contactUpdateSchema,
  enquiryUpdateSchema,
  validateForm,
  getFieldError,
};
